import { forwardRef, ReactElement, Ref } from "react";
import cx from "clsx";
import { Box } from "@twilio-paste/core";
import { SearchIcon } from "@twilio-paste/icons/esm/SearchIcon";

import { types } from "./definitions";
import { ComboboxSearch } from "./sub";

import styles from "./SearchInput.module.css";
import TrackingButton from "../trackingButton";

const SearchInput = forwardRef(
  (
    {
      id,
      width,
      value,
      label = "",
      required = false,
      hasError = false,
      helpText = "",
      inputWidth = "100%",
      submitVariant = types.SearchInputSubmitVariant.button,
      onChange,
      onSelectItem,
      onSubmit,
      errorText,
      isLoading = false,
      placeholder = "",
      // Paste recommendation: https://paste.twilio.design/components/textarea/#positioning-form-fields
      marginBottom = "space80",
      onTrackSearchSubmit,
      querySuggestions = [],
      querySuggestionsItemRender,
      selectedItem,
    }: types.SearchInputProps,
    ref: Ref<HTMLInputElement>
  ): ReactElement => {
    const isUsingExternalSubmitButton =
      submitVariant === types.SearchInputSubmitVariant.button;

    const renderSubmitButton = (): ReactElement => (
      <TrackingButton
        variant={isUsingExternalSubmitButton ? "primary" : "secondary_icon"}
        type="submit"
        trackEvent={onTrackSearchSubmit}
        data-testid="continue-button"
        loading={isLoading}
      >
        <SearchIcon
          decorative={false}
          size="sizeIcon30"
          title="Search input icon"
        />
      </TrackingButton>
    );

    return (
      <Box
        as="form"
        className={styles.searchInputContainer}
        onSubmit={onSubmit}
        marginBottom={marginBottom}
        width={width}
      >
        <Box width={inputWidth}>
          <ComboboxSearch
            id={id}
            value={value}
            onChange={onChange}
            onSelectItem={onSelectItem}
            items={querySuggestions}
            optionTemplate={querySuggestionsItemRender}
            labelText={label}
            helpText={(hasError && errorText) || helpText}
            hasError={hasError}
            placeholder={placeholder}
            required={required}
            ref={ref}
            selectedItem={selectedItem}
            insertAfter={!isUsingExternalSubmitButton && renderSubmitButton()}
          />
        </Box>

        {isUsingExternalSubmitButton && (
          <Box
            className={cx({
              [styles.searchInputButtonContainer]: true,
              [styles.searchInputButtonContainerHelpText]: !!helpText,
              [styles.searchInputButtonContainerError]: hasError,
            })}
          >
            {renderSubmitButton()}
          </Box>
        )}
      </Box>
    );
  }
);

export default SearchInput;
